import type { I18n } from '@funkey/locales';
import { ErrorCode, useDropzone, type FileRejection } from 'react-dropzone';
import type { ClassNameValue } from 'tailwind-merge';
import { Icon } from '~/components/primitives';
import { cn } from '~/utils';

/** 5MiB */
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
/** 25MiB */
const MAX_VIDEO_SIZE = 25 * 1024 * 1024;

export const _DropzoneInput: React.FC<{
    maxFiles: number;
    maxBytes: number;

    minWidth?: number;
    minHeight?: number;

    mimeTypes: Array<keyof typeof MIME_EXTENSIONS>;

    onDropAccepted: (files: File[]) => void;
    onDropRejected: (files: FileRejection[]) => void;

    isUploading: boolean;

    i18n: I18n;
    className?: ClassNameValue;
    disabled?: boolean;
}> = props => {
    const dropzone = useDropzone({
        maxFiles: props.maxFiles,
        multiple: props.maxFiles > 1,
        disabled: props.disabled,
        accept: props.mimeTypes.reduce(
            (acc, mime) => ({ ...acc, [mime]: MIME_EXTENSIONS[mime] }),
            {},
        ),
        validator: file => {
            const maxSize = file.type.includes('video')
                ? MAX_VIDEO_SIZE
                : props.maxBytes || MAX_IMAGE_SIZE;

            if (file.size > maxSize) {
                return {
                    code: ErrorCode.FileTooLarge,
                    message: '' /* Message is handled elsewhere */,
                };
            }

            return null;
        },
        onDropAccepted: async acceptedFiles => {
            const validFiles = [];
            const rejectedFiles = [];

            if (!props.minWidth && !props.minHeight) return props.onDropAccepted(acceptedFiles);

            for (const file of acceptedFiles) {
                // eslint-disable-next-line no-await-in-loop
                const dimensionError = await checkImageDimensions(
                    file,
                    props.minWidth ?? 0,
                    props.minHeight ?? 0,
                    props.i18n,
                );

                if (dimensionError) {
                    rejectedFiles.push({ file, errors: [dimensionError] });
                } else {
                    validFiles.push(file);
                }
            }

            if (rejectedFiles.length > 0) props.onDropRejected(rejectedFiles);
            if (validFiles.length > 0) props.onDropAccepted(validFiles);
        },
        onDropRejected: props.onDropRejected,
    });

    return (
        <div
            {...dropzone.getRootProps()}
            className={cn(
                'mb-5 mt-2 flex justify-center rounded-lg border px-6 py-10',
                dropzone.isDragActive ? 'border-primary' : 'border-gray-900/25 border-dashed',
                props.className,
            )}
        >
            <div className="text-center">
                <Icon
                    color={dropzone.isDragActive ? 'bg-primary-100' : 'bg-gray-100'}
                    name={props.isUploading ? 'spinner' : 'upload'}
                    aria-hidden="true"
                    className={`mx-auto ${props.isUploading ? 'animate-spin' : ''}`}
                />
                <div className="mt-4 flex items-center text-sm leading-6 text-gray-600">
                    <span className="relative cursor-pointer rounded-md font-semibold text-secondary-600 hover:text-secondary">
                        <span>
                            {props.isUploading
                                ? props.i18n._('portal.dropzone.uploading')
                                : props.i18n._('Upload a file')}
                        </span>
                        <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            {...dropzone.getInputProps()}
                        />
                    </span>
                    {!props.isUploading && (
                        <p className="pl-1">{props.i18n._('Or drag and drop')}</p>
                    )}
                </div>

                <p className="text-xs leading-5 text-gray-600">
                    {props.mimeTypes.map(mime => MIME_EXTENSIONS[mime]).join(', ')}{' '}
                    {props.i18n._('up to')} {formatBytes(props.maxBytes)}.
                </p>
            </div>
        </div>
    );
};

export type MimeType = keyof typeof MIME_EXTENSIONS;
const MIME_EXTENSIONS = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/svg+xml': ['.svg'],
    'application/pdf': ['.pdf'],

    'video/mp4': ['.mp4'],

    'application/msword': ['.doc'], // Older MS Word format
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
} as const;

const checkImageDimensions = (
    file: File,
    minWidth: number,
    minHeight: number,
    i18n: I18n,
): Promise<null | { code: string; message: string }> =>
    new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = event => {
            // Video don't need to be checked for dimensions, since we won't use a thumbnail for now
            if (file.type.startsWith('video/')) {
                resolve(null);
            }
            const img = new Image();
            img.src = event.target!.result as string;

            img.onload = () => {
                if (
                    minWidth > 0 &&
                    img.width < minWidth &&
                    minHeight > 0 &&
                    img.height < minHeight
                ) {
                    resolve({
                        code: 'dimensions-too-small',
                        message: i18n._(
                            `Image dimensions must be at least {minWidth}x{minHeight}px.`,
                            { minWidth, minHeight },
                        ),
                    });
                }
                if (minWidth > 0 && img.width < minWidth) {
                    resolve({
                        code: 'width-too-small',
                        message: i18n._(`Image width must be at least {minWidth}px.`, {
                            minWidth,
                        }),
                    });
                } else if (minHeight > 0 && img.height < minHeight) {
                    resolve({
                        code: 'height-too-small',
                        message: i18n._(`Image height must be at least {minHeight}px.`, {
                            minHeight,
                        }),
                    });
                } else {
                    resolve(null);
                }
            };

            img.onerror = () => {
                resolve({
                    code: 'file-invalid-type',
                    message: 'File is not a valid image.',
                });
            };
        };

        reader.readAsDataURL(file);
    });

function formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(decimals))} ${sizes[i]}`;
}
